import { gql } from '@apollo/client';

const CART_TOTALS_FRAGMENT = gql`
  fragment CartTotals on Magento2_CartPrices {
    subtotalExcludingTax {
      currency
      value
    }
    subtotalIncludingTax {
      currency
      value
    }
    discounts {
      amount {
        currency
        value
      }
      label
    }
    subtotalWithDiscountExcludingTax {
      currency
      value
    }
    appliedTaxes {
      label
      amount {
        currency
        value
      }
    }
    grandTotal {
      currency
      value
    }
  }
`;

const CART_UDATE_RESULT_FRAGMENT = gql`
  ${CART_TOTALS_FRAGMENT}

  fragment CartUpdateResult on Magento2_Cart {
    id
    isVirtual
    items {
      uid
      quantity
      prices {
        price {
          currency
          value
        }
        priceIncludingTax {
          currency
          value
        }
        rowTotalIncludingTax {
          currency
          value
        }
        rowTotal {
          currency
          value
        }
      }
      product {
        uid
        stockStatus
      }
      __typename
    }
    prices {
      ...CartTotals
    }
  }
`;

const CART_COUPONS_UPDATE_RESULT_FRAGMENT = gql`
  ${CART_TOTALS_FRAGMENT}

  fragment CartCouponsUpdateResult on Magento2_Cart {
    id
    appliedCoupons {
      code
    }
    prices {
      ...CartTotals
    }
  }
`;

const STORE_CONFIG_FRAGMENT = gql`
  fragment StoreConfig on Magento2_StoreConfig {
    storeCode
    taxSalesDisplayPrice
    taxSalesDisplaySubtotal
  }
`;

export const CART_QUERY = gql`
  ${CART_TOTALS_FRAGMENT}
  ${STORE_CONFIG_FRAGMENT}

  query CartQuery($cartId: String!) {
    magento2 {
      storeConfig {
        ...StoreConfig
      }
      cart(cart_id: $cartId) {
        id
        isVirtual
        appliedCoupons {
          code
        }
        items {
          uid
          quantity
          prices {
            price {
              currency
              value
            }
            priceIncludingTax {
              currency
              value
            }
            rowTotal {
              currency
              value
            }
            rowTotalIncludingTax {
              currency
              value
            }
          }
          product {
            uid
            name
            sku
            stockStatus
            smallImage {
              url
              label
              position
              disabled
            }
          }
          __typename
        }
        prices {
          ...CartTotals
        }
      }
    }
  }
`;

export const ADD_PRODUCTS_TO_CART_MUTATION = gql`
  ${CART_UDATE_RESULT_FRAGMENT}

  mutation AddProductsToCartMutation(
    $cartId: String!
    $cartItems: [Magento2_CartItemInput!]!
  ) {
    magento2 {
      addProductsToCart(cartId: $cartId, cartItems: $cartItems) {
        cart {
          ...CartUpdateResult
        }
        userErrors {
          code
          message
        }
      }
    }
  }
`;

export const UPDATE_CART_ITEMS_MUTATION = gql`
  ${CART_UDATE_RESULT_FRAGMENT}

  mutation UpdateCartItemsMutation(
    $cartId: String!
    $cartItems: [Magento2_CartItemUpdateInput!]!
  ) {
    magento2 {
      updateCartItems(input: { cart_id: $cartId, cart_items: $cartItems }) {
        cart {
          ...CartUpdateResult
        }
      }
    }
  }
`;

export const REMOVE_CART_ITEM_MUTATION = gql`
  ${CART_UDATE_RESULT_FRAGMENT}

  mutation RemoveCartItemMutation($cartId: String!, $cartItemId: ID!) {
    magento2 {
      removeItemFromCart(
        input: { cart_id: $cartId, cart_item_uid: $cartItemId }
      ) {
        cart {
          ...CartUpdateResult
        }
      }
    }
  }
`;

export const APPLY_COUPON_MUTATION = gql`
  ${CART_COUPONS_UPDATE_RESULT_FRAGMENT}

  mutation ApplyCouponMutation($cartId: String!, $couponCode: String!) {
    magento2 {
      applyCouponToCart(input: { cart_id: $cartId, coupon_code: $couponCode }) {
        cart {
          ...CartCouponsUpdateResult
        }
      }
    }
  }
`;

export const REMOVE_COUPON_MUTATION = gql`
  ${CART_COUPONS_UPDATE_RESULT_FRAGMENT}

  mutation RemoveCouponMutation($cartId: String!) {
    magento2 {
      removeCouponFromCart(input: { cart_id: $cartId }) {
        cart {
          ...CartCouponsUpdateResult
        }
      }
    }
  }
`;
